import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, ShoppingCart, Tag, Boxes, Users, FileText,
  LogOut, Plus, Pencil, Trash2, TrendingUp, IndianRupee, Loader2, ExternalLink,
  Paperclip, Download,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar,
} from "recharts";
import { api, inr, TIER_ORDER, tierLabel } from "@/lib/api";
import { PromoManager, LeadsPanel } from "@/components/admin/PromoManager";
import { usePaged, Pager, SearchBox } from "@/components/admin/Paged";
import { Switch } from "@/components/ui/switch";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const TABS = [
  { id: "analytics", label: "Dashboard", icon: LayoutDashboard },
  { id: "products", label: "Products", icon: Package },
  { id: "orders", label: "Orders", icon: ShoppingCart },
  { id: "coupons", label: "Promo Codes", icon: Tag },
  { id: "leads", label: "Catalogue Leads", icon: Download },
  { id: "inventory", label: "Inventory", icon: Boxes },
  { id: "customers", label: "Customers", icon: Users },
  { id: "content", label: "Content", icon: FileText },
];

const ORDER_STATUSES = ["pending", "confirmed", "preparing", "shipped", "delivered", "cancelled"];

const blankProduct = {
  name: "", category: "Signature", collection: "", description: "", long_description: "",
  images: "", ingredients: "", weight_info: "", stock: 50, is_featured: false, is_active: true,
  tier_discounts: {}, prices: {},
};

export default function Admin() {
  const { user, isAdmin, setAuthOpen, logout } = useStore();
  const nav = useNavigate();
  const [tab, setTab] = useState("analytics");

  if (!user) {
    return (
      <Guard title="Admin Access" msg="Please sign in with your authorized admin account.">
        <Button onClick={() => setAuthOpen(true)} className="btn-gold rounded-full" data-testid="admin-signin-btn">Sign In</Button>
      </Guard>
    );
  }
  if (!isAdmin) {
    return (
      <Guard title="Access Restricted" msg="This area is reserved for SCHOKODALE administrators.">
        <Link to="/"><Button className="btn-gold rounded-full">Return Home</Button></Link>
      </Guard>
    );
  }

  return (
    <div className="min-h-screen bg-[#120A07] grid lg:grid-cols-[260px_1fr]" data-testid="admin-panel">
      <aside className="bg-[#1A0F0B] border-r border-[#3B251D] p-5 flex flex-col lg:h-screen lg:sticky lg:top-0">
        <Link to="/" className="flex items-center gap-2 mb-1">
          <img src="/brand/emblem.png" alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D4AF37]/40" />
          <span className="font-display text-2xl gold-gradient-text tracking-wider">SCHOKODALE</span>
        </Link>
        <span className="text-[10px] text-[#8C7B6E] tracking-[0.3em] mb-8">ADMIN CONSOLE</span>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm whitespace-nowrap transition ${tab === t.id ? "bg-[#D4AF37] text-[#1A0F0B] font-semibold" : "text-[#C8B8AB] hover:bg-[#241611]"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="mt-auto pt-6 hidden lg:block">
          <Link to="/" className="flex items-center gap-2 text-[#8C7B6E] text-sm hover:text-[#D4AF37] mb-3"><ExternalLink size={15} /> View Store</Link>
          <button onClick={() => { logout(); nav("/"); }} className="flex items-center gap-2 text-[#8C7B6E] text-sm hover:text-[#DC2626]"><LogOut size={15} /> Sign Out</button>
        </div>
      </aside>

      <main className="p-5 sm:p-8 overflow-x-hidden">
        {tab === "analytics" && <Analytics />}
        {tab === "products" && <Products />}
        {tab === "orders" && <Orders />}
        {tab === "coupons" && <PromoManager />}
        {tab === "leads" && <LeadsPanel />}
        {tab === "inventory" && <Inventory />}
        {tab === "customers" && <Customers />}
        {tab === "content" && <Content />}
      </main>
    </div>
  );
}

const Guard = ({ title, msg, children }) => (
  <div className="min-h-screen bg-[#120A07] flex items-center justify-center px-4" data-testid="admin-guard">
    <div className="lux-card p-10 text-center max-w-md">
      <h1 className="font-display text-3xl gold-gradient-text">{title}</h1>
      <p className="text-[#C8B8AB] mt-3 mb-6">{msg}</p>
      {children}
    </div>
  </div>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="lux-card p-6">
    <div className="flex items-center justify-between">
      <span className="text-[#8C7B6E] text-xs uppercase tracking-wider">{label}</span>
      <Icon size={18} className="text-[#D4AF37]" />
    </div>
    <p className="font-display text-3xl mt-3 text-[#FDFBF7]">{value}</p>
  </div>
);

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  return (
    <div data-testid="admin-analytics">
      <h1 className="font-display text-3xl mb-6">Dashboard</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={IndianRupee} label="Revenue" value={inr(d.revenue)} />
        <Stat icon={ShoppingCart} label="Paid Orders" value={d.paid_count} />
        <Stat icon={Users} label="Customers" value={d.customers} />
        <Stat icon={TrendingUp} label="Avg Order" value={inr(d.aov)} />
      </div>
      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <div className="lux-card p-6">
          <h3 className="font-display text-lg mb-4">Revenue · Last 7 Days</h3>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={d.sales_series}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#3B251D" />
              <XAxis dataKey="date" stroke="#8C7B6E" fontSize={12} />
              <YAxis stroke="#8C7B6E" fontSize={12} />
              <Tooltip contentStyle={{ background: "#241611", border: "1px solid #3B251D", borderRadius: 12, color: "#FDFBF7" }} />
              <Area type="monotone" dataKey="revenue" stroke="#D4AF37" fill="url(#g)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="lux-card p-6">
          <h3 className="font-display text-lg mb-4">Top Products</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={d.top_products}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3B251D" />
              <XAxis dataKey="name" stroke="#8C7B6E" fontSize={10} tickFormatter={(v) => v.split(" ")[0]} />
              <YAxis stroke="#8C7B6E" fontSize={12} />
              <Tooltip contentStyle={{ background: "#241611", border: "1px solid #3B251D", borderRadius: 12, color: "#FDFBF7" }} />
              <Bar dataKey="units" fill="#D4AF37" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="lux-card p-6">
          <h3 className="font-display text-lg mb-4">Recent Orders</h3>
          {d.recent_orders.map((o) => (
            <div key={o.order_id} className="flex justify-between py-2 border-b border-[#3B251D] text-sm">
              <span className="font-mono-lux text-[#D4AF37]">{o.order_id}</span>
              <span className="text-[#C8B8AB] capitalize">{o.status}</span>
              <span className="text-[#FDFBF7]">{inr(o.total)}</span>
            </div>
          ))}
        </div>
        <div className="lux-card p-6">
          <h3 className="font-display text-lg mb-4">Low Stock Alert</h3>
          {d.low_stock.length === 0 ? <p className="text-[#8C7B6E] text-sm">All products well stocked.</p> :
            d.low_stock.map((p) => (
              <div key={p.product_id} className="flex justify-between py-2 border-b border-[#3B251D] text-sm">
                <span className="text-[#C8B8AB]">{p.name}</span>
                <span className={p.stock <= 0 ? "text-[#DC2626]" : "text-[#D97706]"}>{p.stock} left</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function Products() {
  const pg = usePaged("/admin/products", 20);
  const list = pg.items;
  const load = pg.load;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankProduct);
  const [editId, setEditId] = useState(null);

  const openNew = () => { setForm(blankProduct); setEditId(null); setOpen(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name, category: p.category, collection: p.collection || "", description: p.description,
      long_description: p.long_description, images: (p.images || []).join("\n"),
      ingredients: p.ingredients, weight_info: p.weight_info, stock: p.stock,
      is_featured: p.is_featured, is_active: p.is_active,
      tier_discounts: { ...(p.tier_discounts || {}) }, prices: { ...p.prices }, tags: p.tags || [],
    });
    setEditId(p.product_id); setOpen(true);
  };

  const save = async () => {
    const prices = {};
    TIER_ORDER.forEach((k) => { const v = form.prices[k]; if (v !== "" && v != null) prices[k] = Number(v); });
    if (!form.name || Object.keys(prices).length === 0) return toast.error("Name and at least one price required");
    const payload = {
      name: form.name, category: form.category, collection: form.collection || null,
      description: form.description, long_description: form.long_description,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      ingredients: form.ingredients, weight_info: form.weight_info,
      stock: Number(form.stock), is_featured: form.is_featured, is_active: form.is_active,
      prices, tags: form.tags || [],
      tier_discounts: Object.fromEntries(Object.entries(form.tier_discounts).filter(([k, v]) => prices[k] != null && Number(v) > 0).map(([k, v]) => [k, Number(v)])),
    };
    try {
      if (editId) await api.put(`/admin/products/${editId}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editId ? "Product updated" : "Product created");
      setOpen(false); load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg?.replace("Value error, ", "") : d || "Save failed");
    }
  };

  const del = async (id) => { if (!window.confirm("Delete this product?")) return; await api.delete(`/admin/products/${id}`); toast.success("Deleted"); load(); };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h1 className="font-display text-3xl">Products</h1>
        <SearchBox value={pg.search} onChange={pg.setSearch} placeholder="Search name or SKD code" testid="admin-products-search" />
        <Button onClick={openNew} data-testid="admin-add-product-btn" className="btn-gold rounded-full"><Plus size={16} className="mr-2" /> Add Product</Button>
      </div>
      <div className="lux-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1A0F0B] text-[#8C7B6E] text-xs uppercase tracking-wider">
            <tr><th className="text-left p-4">Product</th><th className="text-left p-4 hidden sm:table-cell">Category</th><th className="text-left p-4">From</th><th className="text-left p-4">Stock</th><th className="p-4"></th></tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.product_id} className="border-t border-[#3B251D]" data-testid={`admin-product-${p.slug}`}>
                <td className="p-4 flex items-center gap-3"><img src={p.images?.[0]} className="h-10 w-10 rounded-lg object-cover" alt="" /><span className="text-[#FDFBF7]">{p.name}</span></td>
                <td className="p-4 hidden sm:table-cell text-[#C8B8AB]">{p.category}</td>
                <td className="p-4 text-[#D4AF37]">{inr(p.min_price)}{Object.keys(p.tier_discounts || {}).length > 0 && <span className="ml-2 text-[10px] text-[#1A0F0B] bg-[#D4AF37] rounded-full px-2" title={Object.entries(p.tier_discounts).map(([k, v]) => `${tierLabel(k)}: ${v}%`).join(", ")}>offers on {Object.keys(p.tier_discounts).length} size{Object.keys(p.tier_discounts).length > 1 ? "s" : ""}</span>}</td>
                <td className="p-4 text-[#C8B8AB]">{p.stock}</td>
                <td className="p-4 text-right">
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.slug}`} className="text-[#C8B8AB] hover:text-[#D4AF37] mr-3"><Pencil size={16} /></button>
                  <button onClick={() => del(p.product_id)} data-testid={`delete-product-${p.slug}`} className="text-[#C8B8AB] hover:text-[#DC2626]"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager p={pg} testid="admin-products-pager" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product editor</DialogTitle>
          <h2 className="font-display text-2xl mb-4">{editId ? "Edit" : "New"} Product</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <L label="Name"><Input data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" /></L>
            <L label="Category"><Input data-testid="pf-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="inp" /></L>
            <L label="Collection"><Input value={form.collection} onChange={(e) => setForm({ ...form, collection: e.target.value })} className="inp" /></L>
            <L label="Stock"><Input data-testid="pf-stock" type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="inp" /></L>
            <L label="Short Description" span><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="inp" /></L>
            <L label="Long Description" span><Textarea value={form.long_description} onChange={(e) => setForm({ ...form, long_description: e.target.value })} className="inp" /></L>
            <L label="Image URLs (one per line)" span><Textarea data-testid="pf-images" value={form.images} onChange={(e) => setForm({ ...form, images: e.target.value })} className="inp" /></L>
            <L label="Ingredients" span><Input value={form.ingredients} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} className="inp" /></L>
            <L label="Weight Info"><Input value={form.weight_info} onChange={(e) => setForm({ ...form, weight_info: e.target.value })} className="inp" /></L>
          </div>
          <h3 className="eyebrow mt-5 mb-1">MRP & Discount by Pack Size</h3>
          <p className="text-[10px] text-[#8C7B6E] mb-3">Leave MRP blank if a size isn't offered. Each size's discount % is independent (blank = no discount). Promo codes don't apply to discounted sizes.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {TIER_ORDER.map((k) => (
              <div key={k} className="border border-[#3B251D] rounded-lg p-2">
                <span className="text-[10px] text-[#D4AF37] font-semibold">{tierLabel(k)}</span>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  <Input data-testid={`pf-price-${k}`} type="number" placeholder="MRP ₹" value={form.prices[k] ?? ""} onChange={(e) => setForm({ ...form, prices: { ...form.prices, [k]: e.target.value } })} className="inp h-9 text-xs" />
                  <Input data-testid={`pf-discount-${k}`} type="number" min="0" max="90" placeholder="Disc %" value={form.tier_discounts[k] ?? ""} onChange={(e) => setForm({ ...form, tier_discounts: { ...form.tier_discounts, [k]: e.target.value } })} className="inp h-9 text-xs" />
                </div>
                {Number(form.tier_discounts[k]) > 0 && Number(form.prices[k]) > 0 && <p className="text-[10px] text-[#8C7B6E] mt-1">Sells at {inr(Math.round(form.prices[k] * (1 - form.tier_discounts[k] / 100) * 100) / 100)}</p>}
              </div>
            ))}
          </div>
          <div className="flex gap-6 mt-5">
            <label className="flex items-center gap-2 text-sm text-[#C8B8AB]"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({ ...form, is_featured: e.target.checked })} /> Featured</label>
            <label className="flex items-center gap-2 text-sm text-[#C8B8AB]"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
          </div>
          <Button data-testid="pf-save-btn" onClick={save} className="btn-gold rounded-full w-full mt-6">Save Product</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const L = ({ label, span, children }) => (
  <div className={span ? "sm:col-span-2" : ""}><span className="text-[10px] text-[#8C7B6E] uppercase tracking-wider">{label}</span>{children}</div>
);

const CancelFee = ({ o }) => {
  if (o.payment_status !== "paid" || ["shipped", "delivered", "cancelled"].includes(o.status)) return null;
  const hrs = (Date.now() - new Date(o.created_at).getTime()) / 36e5;
  const pct = hrs <= 6 ? 0 : hrs <= 24 ? 25 : 50;
  return <span data-testid={`cancel-fee-${o.order_id}`} className="block text-[10px] text-[#8C7B6E]" title="Per Refund & Cancellation Policy">If cancelled now: {pct}% fee · return {inr(o.total * (1 - pct / 100))}</span>;
};

function Orders() {
  const [status, setStatusFilter] = useState("");
  const pg = usePaged("/admin/orders", 20, { status });
  const list = pg.items;
  const load = pg.load;
  const setStatus = async (id, status) => { await api.put(`/admin/orders/${id}/status`, { status }); toast.success("Status updated"); load(); };
  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h1 className="font-display text-3xl">Orders</h1>
        <div className="flex flex-wrap gap-2">
          <SearchBox value={pg.search} onChange={pg.setSearch} placeholder="Order ID, name, phone, email" testid="admin-orders-search" />
          <select data-testid="admin-orders-status-filter" value={status} onChange={(e) => setStatusFilter(e.target.value)} className="inp h-10 rounded-full px-4">
            <option value="">All statuses</option>{ORDER_STATUSES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      </div>
      <div className="lux-card overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#1A0F0B] text-[#8C7B6E] text-xs uppercase"><tr><th className="text-left p-4">Order</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Total</th><th className="text-left p-4">Payment</th><th className="text-left p-4">Status</th></tr></thead>
          <tbody>
            {list.map((o) => (
              <tr key={o.order_id} className="border-t border-[#3B251D]" data-testid={`admin-order-${o.order_id}`}>
                <td className="p-4 font-mono-lux text-[#D4AF37]">{o.order_id}</td>
                <td className="p-4 text-[#C8B8AB]">{o.address?.name} {o.gift_wrap && <span title={o.gift_message || "Gift order"} className="text-[#D4AF37]">🎁</span>}<br /><span className="text-xs text-[#8C7B6E]">{o.customer_phone}</span></td>
                <td className="p-4 text-[#FDFBF7]">{inr(o.total)}<CancelFee o={o} /></td>
                <td className="p-4"><span className={o.payment_status === "paid" ? "text-[#2E7D32]" : "text-[#D97706]"}>{o.payment_status}</span></td>
                <td className="p-4">
                  <Select value={o.status} onValueChange={(v) => setStatus(o.order_id, v)}>
                    <SelectTrigger className="w-36 bg-[#241611] border-[#3B251D] text-[#FDFBF7] h-9" data-testid={`order-status-${o.order_id}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#241611] border-[#3B251D] text-[#FDFBF7]">{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager p={pg} testid="admin-orders-pager" />
    </div>
  );
}

function Inventory() {
  const pg = usePaged("/admin/products", 20);
  const list = pg.items;
  const load = pg.load;
  const [edits, setEdits] = useState({});
  const save = async (id) => { await api.put(`/admin/inventory/${id}`, { stock: Number(edits[id]) }); toast.success("Stock updated"); setEdits((e) => { const c = { ...e }; delete c[id]; return c; }); load(); };
  return (
    <div data-testid="admin-inventory">
      <h1 className="font-display text-3xl mb-6">Inventory</h1>
      <div className="lux-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#1A0F0B] text-[#8C7B6E] text-xs uppercase"><tr><th className="text-left p-4">Product</th><th className="text-left p-4">Current</th><th className="text-left p-4">Update</th></tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.product_id} className="border-t border-[#3B251D]">
                <td className="p-4 text-[#FDFBF7]">{p.name}</td>
                <td className="p-4"><span className={p.stock <= 10 ? "text-[#D97706]" : "text-[#C8B8AB]"}>{p.stock}</span></td>
                <td className="p-4 flex gap-2 items-center">
                  <Input type="number" data-testid={`inv-input-${p.slug}`} value={edits[p.product_id] ?? p.stock} onChange={(e) => setEdits({ ...edits, [p.product_id]: e.target.value })} className="inp w-28" />
                  <Button data-testid={`inv-save-${p.slug}`} onClick={() => save(p.product_id)} className="btn-outline-gold rounded-lg h-9">Save</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager p={pg} testid="admin-inventory-pager" />
    </div>
  );
}

function Customers() {
  const pg = usePaged("/admin/customers", 25);
  const list = pg.items;
  return (
    <div data-testid="admin-customers">
      <div className="flex flex-wrap gap-3 justify-between items-center mb-6">
        <h1 className="font-display text-3xl">Customers</h1>
        <SearchBox value={pg.search} onChange={pg.setSearch} placeholder="Name, email or phone" testid="admin-customers-search" />
      </div>
      <div className="lux-card overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-[#1A0F0B] text-[#8C7B6E] text-xs uppercase"><tr><th className="text-left p-4">Name</th><th className="text-left p-4">Contact</th><th className="text-left p-4">Orders</th><th className="text-left p-4">Spent</th></tr></thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.user_id} className="border-t border-[#3B251D]">
                <td className="p-4 text-[#FDFBF7]">{u.name}</td>
                <td className="p-4 text-[#C8B8AB]">{u.email || "—"}<br /><span className="text-xs text-[#8C7B6E]">{u.phone || ""}</span></td>
                <td className="p-4 text-[#C8B8AB]">{u.order_count}</td>
                <td className="p-4 text-[#D4AF37]">{inr(u.total_spent)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager p={pg} testid="admin-customers-pager" />
    </div>
  );
}

function CustomServices() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/custom-services").then((r) => setItems(r.data)); }, []);
  const upd = (i, k, v) => setItems(items.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const save = async (s) => {
    const { service_id, order, ...body } = s;
    await api.put(`/admin/custom-services/${service_id}`, body);
    toast.success(`${s.name} saved`);
  };
  return (
    <div data-testid="admin-custom-services">
      <h2 className="font-display text-2xl mb-4">Customisation Menu</h2>
      <div className="grid lg:grid-cols-3 gap-4">
        {items.map((s, i) => (
          <div key={s.service_id} className="lux-card p-4 grid gap-2">
            <Input value={s.name} onChange={(e) => upd(i, "name", e.target.value)} className="inp" />
            <Textarea value={s.description} onChange={(e) => upd(i, "description", e.target.value)} className="inp" />
            <Input placeholder="Pricing label" value={s.pricing} onChange={(e) => upd(i, "pricing", e.target.value)} className="inp" />
            <Input placeholder="Availability" value={s.availability} onChange={(e) => upd(i, "availability", e.target.value)} className="inp" />
            <Input placeholder="Image URL (optional)" value={s.image} onChange={(e) => upd(i, "image", e.target.value)} className="inp" />
            <label className="text-xs text-[#C8B8AB] flex items-center gap-2"><input type="checkbox" checked={s.active} onChange={(e) => upd(i, "active", e.target.checked)} /> Visible on site</label>
            <Button data-testid={`admin-service-save-${s.service_id}`} onClick={() => save(s)} className="btn-gold rounded-full w-max">Save</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const openFile = async (a) => {
  const r = await api.get(`/admin/files/${a.file_id}`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
};

function BannerEditor() {
  const { setConfig } = useStore();
  const [all, setAll] = useState(null);
  const [b, setB] = useState({ enabled: true, eyebrow: "", title: "", subtitle: "" });
  useEffect(() => {
    Promise.all([api.get("/site-content"), api.get("/config")]).then(([sc, cfg]) => { setAll(sc.data); setB(cfg.data.festive_banner); });
  }, []);
  const save = async () => {
    if (b.enabled && !b.title.trim()) return toast.error("Heading is required when the banner is on");
    await api.put("/admin/site-content", { ...all, festive_banner: b });
    setConfig((c) => ({ ...c, festive_banner: b }));
    toast.success(b.enabled ? "Festive banner updated" : "Festive banner hidden");
  };
  if (!all) return null;
  return (
    <div className="lux-card p-6" data-testid="admin-banner-editor">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-2xl">Homepage Festive Banner</h2>
        <label className="flex items-center gap-2 text-sm text-[#C8B8AB]"><Switch data-testid="banner-enabled" checked={b.enabled} onCheckedChange={(v) => setB({ ...b, enabled: v })} /> {b.enabled ? "Visible" : "Hidden"}</label>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <Input data-testid="banner-eyebrow" placeholder="Small label (e.g. Festive Sale)" value={b.eyebrow} onChange={(e) => setB({ ...b, eyebrow: e.target.value })} className="inp" />
        <Input data-testid="banner-title" placeholder="Heading" value={b.title} onChange={(e) => setB({ ...b, title: e.target.value })} className="inp sm:col-span-2" />
        <Input data-testid="banner-subtitle" placeholder="Subtitle" value={b.subtitle} onChange={(e) => setB({ ...b, subtitle: e.target.value })} className="inp sm:col-span-3" />
      </div>
      <p className="text-[11px] text-[#8C7B6E] mt-2">Display only — this banner does not apply any discount or promo code.</p>
      <Button data-testid="banner-save-btn" onClick={save} className="btn-gold rounded-full mt-4">Save Banner</Button>
    </div>
  );
}

function Content() {
  const [posts, setPosts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", cover_image: "" });
  const load = () => { api.get("/admin/blog").then((r) => setPosts(r.data)); api.get("/admin/gift-requests").then((r) => setRequests(r.data)); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.title) return toast.error("Title required");
    await api.post("/admin/blog", { ...form, published: true });
    toast.success("Post published"); setForm({ title: "", excerpt: "", content: "", cover_image: "" }); load();
  };
  const del = async (id) => { await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="admin-content" className="space-y-10">
      <BannerEditor />
      <CustomServices />
      <div>
        <h1 className="font-display text-3xl mb-6">Journal / Blog</h1>
        <div className="lux-card p-6 mb-6 grid gap-3">
          <Input data-testid="blog-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="inp" />
          <Input placeholder="Cover image URL" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} className="inp" />
          <Input placeholder="Excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="inp" />
          <Textarea placeholder="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="inp" />
          <Button data-testid="blog-create-btn" onClick={create} className="btn-gold rounded-full w-max">Publish Post</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {posts.map((p) => (
            <div key={p.post_id} className="lux-card p-4 flex justify-between items-center">
              <span className="text-[#FDFBF7]">{p.title}</span>
              <button onClick={() => del(p.post_id)} className="text-[#8C7B6E] hover:text-[#DC2626]"><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="font-display text-2xl mb-4">Gift, Corporate & Customisation Enquiries</h2>
        <div className="lux-card overflow-x-auto">
          <table className="w-full text-sm min-w-[560px]">
            <thead className="bg-[#1A0F0B] text-[#8C7B6E] text-xs uppercase"><tr><th className="text-left p-4">Type</th><th className="text-left p-4">Name</th><th className="text-left p-4">Contact</th><th className="text-left p-4">Qty</th><th className="text-left p-4">Message</th></tr></thead>
            <tbody>
              {requests.length === 0 && <tr><td className="p-4 text-[#8C7B6E]" colSpan={5}>No requests yet.</td></tr>}
              {requests.map((r) => (
                <tr key={r.request_id} className="border-t border-[#3B251D]">
                  <td className="p-4 capitalize text-[#D4AF37]">{r.type}</td>
                  <td className="p-4 text-[#FDFBF7]">{r.name}{r.company ? <><br /><span className="text-xs text-[#8C7B6E]">{r.company}</span></> : null}</td>
                  <td className="p-4 text-[#C8B8AB]">{r.email}<br /><span className="text-xs text-[#8C7B6E]">{r.phone}</span></td>
                  <td className="p-4 text-[#C8B8AB]">{r.quantity}</td>
                  <td className="p-4 text-[#C8B8AB] max-w-xs">
                    {r.service && <span className="block text-[#D4AF37]">{r.service}</span>}
                    {[["Flavour", r.flavour], ["Design", r.design_details], ["Packaging", r.packaging], ["City", r.delivery_city]].filter(([, v]) => v).map(([l, v]) => <span key={l} className="block text-xs text-[#8C7B6E]">{l}: {v}</span>)}
                    {r.message}
                    {r.attachments?.map((a) => (
                      <button key={a.file_id} onClick={() => openFile(a)} data-testid={`admin-attachment-${a.file_id}`} className="block text-xs text-[#D4AF37] underline mt-1 truncate max-w-[220px]"><Paperclip size={12} className="inline mr-1" />{a.name}</button>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Loading = () => <div className="py-32 text-center text-[#8C7B6E]"><Loader2 className="animate-spin inline" /></div>;
